import { HiOutlineArrowRight } from 'react-icons/hi';
import { PiSparkleFill } from 'react-icons/pi';

const STYLE = `
@keyframes na-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
@keyframes na-in { 0% { opacity: 0; transform: translateY(14px) scale(.96); } 100% { opacity: 1; transform: none; } }
@keyframes na-twinkle { 0%, 100% { opacity: 0; transform: scale(.3) rotate(0deg); } 50% { opacity: 1; transform: scale(1) rotate(45deg); } }
@keyframes na-pulse { 0%, 100% { transform: scale(1); opacity: .9; } 50% { transform: scale(1.05); opacity: 1; } }
@keyframes na-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
@keyframes na-check { 0% { transform: scale(0); } 60% { transform: scale(1.2); } 100% { transform: scale(1); } }
.na-card { opacity: 0; animation: na-in .6s cubic-bezier(.32,.72,0,1) forwards, na-float 4.5s ease-in-out infinite; }
.na-line { background: linear-gradient(90deg, #E4E6FB 25%, #F3F4FE 50%, #E4E6FB 75%); background-size: 200% 100%; animation: na-shimmer 2.4s linear infinite; }
.na-spark { opacity: 0; animation: na-twinkle 3s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
	.na-card, .na-line, .na-spark, .na-blob, .na-check { animation: none !important; opacity: 1; }
	.na-spark { opacity: 0; }
}
`;

// A compact skeleton "job card": company tile, two shimmering lines and an apply pill.
const SkeletonCard = ({ color, delay, offset, apply }: { color: string; delay: number; offset: number; apply?: boolean }) => (
	<div
		className="na-card relative flex w-[210px] items-center gap-2.5 rounded-[12px] border border-[#E8E8F0] bg-white px-3 py-2 shadow-[0_4px_14px_rgba(60,50,180,0.07)]"
		style={{ animationDelay: `${delay}s, ${delay + 0.6}s`, marginLeft: offset }}
	>
		<span className="h-7 w-7 shrink-0 rounded-[8px]" style={{ background: color }} />
		<div className="flex-1 space-y-1.5">
			<div className="na-line h-2 w-4/5 rounded-full" />
			<div className="na-line h-1.5 w-3/5 rounded-full" />
		</div>
		<span className="h-5 w-10 rounded-[7px] bg-gradient-to-b from-[#6A5FF2] to-[#3F36D8] opacity-90" />
		{apply ? (
			<span className="na-check absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#2E9B5B] text-white shadow-md" style={{ animation: 'na-check .5s ease-out 1.6s both' }}>
				<svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
					<path d="M20 6 9 17l-5-5" />
				</svg>
			</span>
		) : null}
	</div>
);

// Illustration, one line of copy and the CTA are packed tight so the button is always visible without scrolling.
const NoApplicationsContainer = () => {
	return (
		<div className="mt-6 flex flex-col items-center px-4 pb-6 text-center">
			<style dangerouslySetInnerHTML={{ __html: STYLE }} />

			<div className="relative flex h-[190px] w-[260px] items-center justify-center">
				<div className="absolute inset-0 flex items-center justify-center">
					<div
						className="na-blob h-[190px] w-[190px] rounded-full bg-gradient-to-b from-[#EEEDFD] via-[#F4F3FF] to-transparent"
						style={{ animation: 'na-pulse 5s ease-in-out infinite' }}
					/>
				</div>
				<div className="relative flex flex-col gap-2">
					<SkeletonCard color="#4F46E5" delay={0.1} offset={-10} />
					<SkeletonCard color="#0EA5E9" delay={0.3} offset={14} apply />
					<SkeletonCard color="#F59E0B" delay={0.5} offset={-4} />
				</div>
				<PiSparkleFill className="na-spark absolute left-2 top-4 h-4 w-4 text-[#8F89F5]" style={{ animationDelay: '0.4s' }} />
				<PiSparkleFill className="na-spark absolute right-1 top-10 h-3.5 w-3.5 text-[#AC5DD9]" style={{ animationDelay: '1.4s' }} />
				<PiSparkleFill className="na-spark absolute bottom-6 right-4 h-4 w-4 text-[#6A5FF2]" style={{ animationDelay: '2.2s' }} />
			</div>

			<h3 className="mt-3 text-[20px] leading-[28px] font-[800] tracking-[-0.02em] text-[#111E2C] dark:text-[#FAFAFC]">No applications yet</h3>
			<p className="mt-1 max-w-[380px] text-[14px] leading-[21px] text-[#6B6C85] dark:text-[#C7C9D9]">
				Jobs you apply to or get matched with will show up here.
			</p>
			<button
				className="mt-4 inline-flex items-center justify-center gap-2 rounded-[12px] bg-gradient-to-b from-[#5B4FF0] to-[#3027D8] px-6 py-2.5 text-[14px] font-[700] text-white shadow-[0_6px_16px_rgba(79,70,229,0.28)] transition-transform hover:-translate-y-0.5"
				onClick={() => {
					window.open(
						'https://www.masaischool.com?utm_source=leveluphome&utm_medium=website&utm_campaign=sendtowebsite',
						'_blank',
						'noopener,noreferrer',
					);
				}}
			>
				Explore our courses
				<HiOutlineArrowRight className="h-4 w-4" />
			</button>
		</div>
	);
};

export default NoApplicationsContainer;
